import { createAsyncThunk } from "@reduxjs/toolkit";
import { AxiosError, isAxiosError } from "axios";
import { displayAlert } from "../slice/alertSlice.tsx";
import { handleThunkError } from "./handleThunkError";
import personasApi from "../api/personasApi.tsx";
import {
  CoreValue,
  CoreValuesParams,
  GenerateAIPersonaData,
  PersonasParams,
  PersonaUpdatePayload,
} from "../types/personasTypes.tsx";
import { PersonaAPIResponseData } from "../types/personasTypes.tsx";

interface ApiSimpleError {
  detail: string;
}

type ApiValidationError = Record<string, string[]>;

export const fetchPersonasData = createAsyncThunk<
  PersonaAPIResponseData[],
  PersonasParams | undefined,
  { rejectValue: string }
>("personas/fetchListData", async (params, thunkAPI) => {
  try {
    const response = await personasApi.fetchPersonasData(params);
    return response.data;
  } catch (e: unknown) {
    handleThunkError(e, thunkAPI.dispatch, "Failed to fetch personas list.");
    return thunkAPI.rejectWithValue("Failed to fetch personas list.");
  }
});

export const fetchPersonaByIdThunk = createAsyncThunk<
  PersonaAPIResponseData,
  string | number,
  { rejectValue: string }
>("personas/fetchById", async (personaId, thunkAPI) => {
  try {
    const response = await personasApi.fetchPersonaById(personaId);
    return response.data;
  } catch (e: unknown) {
    handleThunkError(
      e,
      thunkAPI.dispatch,
      `Failed to fetch persona ${personaId}.`,
    );
    return thunkAPI.rejectWithValue(`Failed to fetch persona ${personaId}.`);
  }
});

export const createPersonaThunk = createAsyncThunk<
  PersonaAPIResponseData,
  { data: FormData; callback?: () => void },
  { rejectValue: ApiValidationError | string }
>("personas/createPersona", async ({ data, callback }, thunkAPI) => {
  try {
    const response = await personasApi.createPersona(data);
    if (callback) {
      callback();
    }
    thunkAPI.dispatch(
      displayAlert({ type: "success", text: "Persona created successfully!" }),
    );
    return response.data;
  } catch (e: unknown) {
    if (isAxiosError(e) && e.response?.status === 429)
      return thunkAPI.rejectWithValue("Rate limited.");
    if (e instanceof AxiosError && e.response?.data) {
      const errorData = e.response.data;
      if (typeof errorData === "object" && !("detail" in errorData)) {
        const messages = Object.entries(errorData as ApiValidationError)
          .map(([key, value]) => `${key}: ${value.join(", ")}`)
          .join("; ");
        thunkAPI.dispatch(displayAlert({ type: "error", text: messages }));
        return thunkAPI.rejectWithValue(errorData as ApiValidationError);
      }
      const errorDetail =
        (errorData as ApiSimpleError).detail || "Failed to create persona.";
      thunkAPI.dispatch(displayAlert({ type: "error", text: errorDetail }));
      return thunkAPI.rejectWithValue(errorDetail);
    }
    thunkAPI.dispatch(
      displayAlert({ type: "error", text: "An unexpected error occurred." }),
    );
    return thunkAPI.rejectWithValue("An unexpected error occurred.");
  }
});

export const deletePersonaThunk = createAsyncThunk<
  string,
  string,
  { rejectValue: string }
>("personas/deletePersona", async (personaId, thunkAPI) => {
  try {
    await personasApi.deletePersona(personaId);
    thunkAPI.dispatch(
      displayAlert({ type: "success", text: "Persona deleted successfully!" }),
    );
    return personaId;
  } catch (e: unknown) {
    handleThunkError(e, thunkAPI.dispatch, "Failed to delete persona.");
    return thunkAPI.rejectWithValue("Failed to delete persona.");
  }
});

export const updatePersonaThunk = createAsyncThunk<
  PersonaAPIResponseData,
  {
    id: string | number;
    data: FormData | Partial<PersonaUpdatePayload>;
    callback?: () => void;
  },
  { rejectValue: string | object }
>("personas/updatePersona", async ({ id, data, callback }, thunkAPI) => {
  try {
    const response = await personasApi.updatePersona(id, data);
    if (callback) {
      callback();
    }
    thunkAPI.dispatch(
      displayAlert({ type: "success", text: "Persona updated successfully!" }),
    );
    return response.data;
  } catch (e: unknown) {
    if (isAxiosError(e) && e.response?.status === 429)
      return thunkAPI.rejectWithValue("Rate limited.");
    if (e instanceof AxiosError && e.response?.data) {
      const errorData = e.response.data;
      if (typeof errorData === "object" && !("detail" in errorData)) {
        const messages = Object.entries(errorData as ApiValidationError)
          .map(
            ([key, value]) =>
              `${key}: ${Array.isArray(value) ? value.join(", ") : value}`,
          )
          .join("; ");
        thunkAPI.dispatch(displayAlert({ type: "error", text: messages }));
        return thunkAPI.rejectWithValue(errorData as ApiValidationError);
      }
      const errorDetail =
        (errorData as ApiSimpleError).detail || "Failed to update persona.";
      thunkAPI.dispatch(displayAlert({ type: "error", text: errorDetail }));
      return thunkAPI.rejectWithValue(errorDetail);
    }
    thunkAPI.dispatch(
      displayAlert({ type: "error", text: "An unexpected error occurred." }),
    );
    return thunkAPI.rejectWithValue("An unexpected error occurred.");
  }
});

export const generateAIPersonaThunk = createAsyncThunk<
  PersonaAPIResponseData[],
  GenerateAIPersonaData,
  { rejectValue: string }
>("personas/generate", async (generateData, thunkAPI) => {
  try {
    const response = await personasApi.generateAIPersona(generateData);
    return response.data;
  } catch (e: unknown) {
    handleThunkError(e, thunkAPI.dispatch, "Failed to generate personas.");
    return thunkAPI.rejectWithValue("Failed to generate personas.");
  }
});

export const fetchCoreValues = createAsyncThunk<
  CoreValue[],
  CoreValuesParams | undefined,
  { rejectValue: string }
>("personas/fetchCoreValues", async (params, thunkAPI) => {
  try {
    const response = await personasApi.fetchCoreValues(params);
    return response.data;
  } catch (e: unknown) {
    handleThunkError(e, thunkAPI.dispatch, "Failed to fetch core values.");
    return thunkAPI.rejectWithValue("Failed to fetch core values.");
  }
});
